import React from "react";
import { List } from "immutable";
import HistoryNode from "./HistoryNode";
import { generateID } from "./helpers.js";
import { getAllHistoryNodesSortedByTimestamp } from "./history.js";

export const CURRENT_SERIALIZATION_VERSION = 0.5;

export function serializeDocument({ palette, selectedSwatch, selectedTool, undos, redos, currentHistoryNode }) {
	// serialize tools as code (+ identifiers), and create a sandbox?

	const serializeOperation = (operation) => {
		return {
			id: operation.id,
			toolID: operation.tool.name,
			points: operation.points,
			swatch: operation.swatch,
		};
	};
	const allHistoryNodes = getAllHistoryNodesSortedByTimestamp(currentHistoryNode);

	const toID = (historyNode) => historyNode.id;

	const historyNodesByID = {};
	allHistoryNodes.forEach((historyNode) => {
		historyNodesByID[historyNode.id] = {
			parentHNID: historyNode.parentNode && historyNode.parentNode.id,
			childHNIDs: historyNode.childNodes.map(toID),
			timestamp: historyNode.timestamp,
			operation: historyNode.operation && serializeOperation(historyNode.operation),
			name: historyNode.name,
			id: historyNode.id,
		};
	});

	return {
		format: "BuzzerDrawing",
		formatVersion: CURRENT_SERIALIZATION_VERSION,
		palette: palette,
		selectedSwatch: selectedSwatch,
		selectedToolID: selectedTool.name,
		historyNodesByID,
		undoHNIDs: undos.toJS().map(toID),
		redoHNIDs: redos.toJS().map(toID),
		currentHNID: toID(currentHistoryNode),
	};
}

export function deserializeDocument(serialized, isFromFile, getToolByName) {
	const nounPhraseThingToLoad = isFromFile ? "document" : "document state";
	if (serialized.format !== "BuzzerDrawing") {
		return [{
			message: `Can't load ${nounPhraseThingToLoad} - it does not appear to be a BuzzerDrawing document`,
		}];
	}
	if (
		typeof serialized.formatVersion !== "number" ||
		serialized.formatVersion > CURRENT_SERIALIZATION_VERSION
	) {
		return [{
			message: `Can't load ${nounPhraseThingToLoad} created by later version of the app`,
		}];
	}
	const MINIMUM_LOADABLE_VERSION = 0.1;


	if (serialized.formatVersion === 0.1) {
		serialized.formatVersion = 0.3;
	}
	if (serialized.formatVersion === 0.3) {
		serialized.formatVersion = 0.4;

		const baseTimestamp = +new Date("March,1 2024 00:00:00");
		const rootHistoryNode = {
			name: "New Document",
			id: generateID(),
			childHNIDs: [],
			timestamp: baseTimestamp,
		};

		const historyNodesByID = {};
		historyNodesByID[rootHistoryNode.id] = rootHistoryNode;

		const opToHN = new Map();
		const makeFreeFloatingHN = (op) => {
			const id = generateID();
			historyNodesByID[id] = {
				id,
				operation: op,
				name: op.toolID,
				parentHNID: undefined,
				childHNIDs: [],
				timestamp: baseTimestamp,
			};
			opToHN.set(op, historyNodesByID[id]);
		};
		serialized.undos.forEach(makeFreeFloatingHN);
		serialized.redos.forEach(makeFreeFloatingHN);

		let currentHNID;
		const undoHNIDs = [];
		const redoHNIDs = [];
		const linkHNs = (parentHN, childHN) => {
			if (parentHN) {
				parentHN.childHNIDs.push(childHN.id);
				childHN.parentHNID = parentHN.id;
			}
		};

		const orderedOps = [
			...serialized.undos,
			...[...serialized.redos].reverse(),
		];
		const orderedHNs = [
			rootHistoryNode,
			...orderedOps.map((op) => opToHN.get(op)),
		];
		orderedHNs.forEach((historyNode, index, orderedHNs) => {
			const previousHistoryNode = orderedHNs[index - 1];
			if (previousHistoryNode) {
				linkHNs(previousHistoryNode, historyNode);
			}
			// TODO: update the history view's sort to work structurally when timestamps are equal
			historyNode.timestamp += index;
		});

		undoHNIDs.push(rootHistoryNode.id);
		orderedOps.forEach((op) => {
			const historyNode = opToHN.get(op);
			if (op === serialized.undos[serialized.undos.length - 1]) {
				currentHNID = historyNode.id;
			} else if (serialized.redos.indexOf(op) > -1) {
				redoHNIDs.push(historyNode.id);
			} else {
				undoHNIDs.push(historyNode.id);
			}
		});

		serialized.historyNodesByID = historyNodesByID;
		serialized.currentHNID = currentHNID;
		serialized.undoHNIDs = undoHNIDs;
		serialized.redoHNIDs = redoHNIDs;

		delete serialized.undos;
		delete serialized.redos;
	}
	if (serialized.formatVersion === 0.4) {
		serialized.formatVersion = 0.5;

		for (const historyNode of Object.values(serialized.historyNodesByID)) {
			if (historyNode.operation?.name === "Fill") {
				const { points } = historyNode.operation;

				points.length = 1;
			}
		}
	}
	if (serialized.formatVersion < CURRENT_SERIALIZATION_VERSION) {
		return [{
			message: <React.Fragment>
				<p>
					Can’t load {nounPhraseThingToLoad} {
						serialized.formatVersion
					} to {CURRENT_SERIALIZATION_VERSION}{
						MINIMUM_LOADABLE_VERSION !== CURRENT_SERIALIZATION_VERSION
							? ` (minimum loadable: ${MINIMUM_LOADABLE_VERSION})`
							: ""
					}
				</p>
			</React.Fragment>
		}];
	}

	const expectPropertiesToExist = (properties, object, locationMessage) => {
		properties.forEach((key) => {
			if (!object[key]) {
				throw new TypeError(`expected property '${key}' ${locationMessage}`);
			}
		});
	};
	const deserializeOperation = (serializedOperation) => {
		expectPropertiesToExist(
			["id", "toolID", "points", "swatch"],
			serializedOperation,
			`on operation with ID ${serializedOperation.id}`,
		);
		const tool = getToolByName(serializedOperation.toolID);
		return {
			id: serializedOperation.id,
			tool: tool,
			points: serializedOperation.points,
			swatch: serializedOperation.swatch,
		};
	};
	expectPropertiesToExist(
		["palette", "selectedSwatch", "selectedToolID", "undoHNIDs", "redoHNIDs", "currentHNID", "historyNodesByID"],
		serialized,
		"on the root document object",
	);
	const historyNodesByID = {};
	for (const [historyNodeID, historyNodeData] of Object.entries(serialized.historyNodesByID)) {
		historyNodesByID[historyNodeID] = new HistoryNode(historyNodeData);
		if (historyNodeData.operation) {
			historyNodesByID[historyNodeID].operation = deserializeOperation(historyNodeData.operation);
		}
	}
	const fromHNID = (historyNodeID) => historyNodesByID[historyNodeID];
	for (const [historyNodeID, historyNodeData] of Object.entries(serialized.historyNodesByID)) {
		historyNodesByID[historyNodeID].parentNode = historyNodesByID[historyNodeData.parentHNID];
		historyNodesByID[historyNodeID].childNodes = historyNodeData.childHNIDs.map(fromHNID);
	}
	return [null, {
		palette: serialized.palette,
		selectedSwatch: serialized.selectedSwatch,
		selectedTool: getToolByName(serialized.selectedToolID),
		undos: new List(serialized.undoHNIDs.map(fromHNID)),
		redos: new List(serialized.redoHNIDs.map(fromHNID)),
		currentHistoryNode: fromHNID(serialized.currentHNID),
	}];
}
